import {
  ChevronLeft,
  ChevronRight,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Image03,
  Typography,
} from '@v2/ui';
import { cn } from '@v2/utils';
import Image from 'next/image';
import { ReactNode, useRef, useState } from 'react';
import Masonry from 'react-responsive-masonry';
import { useOnClickOutside } from 'usehooks-ts';

type Props = {
  images: string[];
};

const SWIPE_THRESHOLD = 50;

function ImagesLightbox({
  images,
  children,
}: Props & {
  children: (openAt: (index: number) => void) => ReactNode;
}) {
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const handleDecrement = () => {
    if (currentStep === 0) return;

    setCurrentStep((state) => state - 1);
  };

  const handleIncrement = () => {
    if (currentStep === images.length - 1) return;

    setCurrentStep((state) => state + 1);
  };

  const openAt = (index: number) => {
    setCurrentStep(index);
    setIsOpen(true);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;

    const delta = event.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;

    if (delta < 0) handleIncrement();
    else handleDecrement();
  };

  useOnClickOutside(imageContainerRef, () => setIsOpen(false));

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {children(openAt)}
      <DialogContent
        aria-describedby={undefined}
        className="md:max-w-screen h-screen w-screen items-center justify-center gap-0 rounded-none bg-transparent p-0 md:grid"
      >
        <DialogTitle className="sr-only">Hotel photos</DialogTitle>
        <div
          ref={imageContainerRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="absolute left-4 top-1/2 z-10 flex h-full min-h-fit -translate-y-1/2 transform items-center">
            <button
              className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-gray-800 shadow-md disabled:hidden"
              aria-label="Previous photo"
              disabled={currentStep === 0}
              onClick={handleDecrement}
            >
              <ChevronLeft />
            </button>
          </div>
          <div className="flex items-center justify-center md:h-[720px] md:max-w-[1080px]">
            <Image
              src={images[currentStep]}
              alt={'Photo of the hotel'}
              width="0"
              height="0"
              sizes="(max-width: 1080px) 100vw, 1080px"
              loading="lazy"
              className="h-[70dvh] w-full object-cover object-center md:h-[720px]"
            />
          </div>
          <div className="absolute right-4 top-1/2 z-10 flex h-full min-h-fit w-12 -translate-y-1/2 transform items-center">
            <button
              className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-gray-800 shadow-md disabled:opacity-50 lg:mr-[-20px]"
              aria-label="Next photo"
              disabled={currentStep === images.length - 1}
              onClick={handleIncrement}
            >
              <ChevronRight />
            </button>
          </div>
          <Typography
            size="tsm"
            className="absolute bottom-4 left-0 right-0 z-0 mb-4 flex items-end justify-center font-semibold text-white"
          >
            Picture {currentStep + 1} of {images.length}
          </Typography>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function HotelMobileImages({
  mainImage,
  images,
}: {
  mainImage: string;
  images: string[];
}) {
  const gallery = images?.length ? images : mainImage ? [mainImage] : [];

  if (!gallery.length)
    return (
      <div className="relative min-h-[200px] w-full">
        {mainImage ? (
          <Image
            src={mainImage}
            className="object-cover"
            alt="Room photo"
            fill={true}
            priority
            fetchPriority="high"
            sizes="100vw"
          />
        ) : null}
      </div>
    );

  return (
    <ImagesLightbox images={gallery}>
      {(openAt) => (
        <button
          type="button"
          aria-label={
            gallery.length > 1
              ? `View all ${gallery.length} hotel photos`
              : 'View hotel photo'
          }
          className="relative block min-h-[200px] w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
          onClick={() => openAt(0)}
        >
          <Image
            src={mainImage || gallery[0]}
            className="object-cover"
            alt="Room photo"
            fill={true}
            priority
            fetchPriority="high"
            sizes="100vw"
          />
          {gallery.length > 1 && (
            <span className="absolute bottom-3 right-3 flex h-8 items-center gap-x-1.5 rounded-lg bg-white px-2.5 shadow-sm">
              <Image03 className="text-gray-800" />
              <Typography size="txs" className="font-semibold text-gray-800">
                {gallery.length}
              </Typography>
            </span>
          )}
        </button>
      )}
    </ImagesLightbox>
  );
}

export function HotelImagesDialog(props: Props) {
  const { images } = props;

  if (images.length < 5) {
    return (
      <ImagesLightbox images={images}>
        {(openAt) => (
          <Masonry gutter="10px" columnsCount={2}>
            {images.slice(0, 3).map((image, i) => (
              <button
                type="button"
                key={image}
                aria-label={`View hotel photo ${i + 1}`}
                className="w-full"
                onClick={() => openAt(i)}
              >
                <Image
                  alt="Hotel other photos"
                  src={image}
                  width="0"
                  height="0"
                  sizes="(max-width: 768px) 50vw, 640px"
                  priority={i === 0}
                  fetchPriority={i === 0 ? 'high' : undefined}
                  loading={i === 0 ? undefined : 'lazy'}
                  className={cn(
                    'h-[147px] w-full cursor-pointer object-cover object-center'
                  )}
                />
              </button>
            ))}
          </Masonry>
        )}
      </ImagesLightbox>
    );
  }
  return (
    <ImagesLightbox images={images}>
      {(openAt) => (
        <div className="flex min-h-[300px] items-center gap-x-2.5">
          <div className="relative mb-3 h-full w-3/6 flex-shrink-0 rounded-lg bg-cover">
            <DialogTrigger asChild onClick={() => openAt(0)}>
              <button
                aria-label={`View all ${images.length}+ hotel photos`}
                className="absolute left-6 top-6 z-10 flex h-10 items-center gap-x-2.5 rounded-lg bg-white px-3"
              >
                <Image03 className="text-gray-800" />
                <Typography size="tsm" className="font-semibold">
                  {images.length}+
                </Typography>
              </button>
            </DialogTrigger>
            <button
              type="button"
              aria-label="View hotel photo 1"
              className="w-full"
              onClick={() => openAt(0)}
            >
              <Image
                src={images[0]}
                alt={'main Hotel photo'}
                width="0"
                height="0"
                sizes="(max-width: 768px) 100vw, 640px"
                priority
                fetchPriority="high"
                className="h-[300px] w-full cursor-pointer object-cover object-center"
              />
            </button>
          </div>
          <Masonry gutter="10px" columnsCount={2}>
            {images.slice(1, 5).map((image, i) => (
              <button
                type="button"
                key={image}
                aria-label={`View hotel photo ${i + 2}`}
                className="w-full"
                onClick={() => openAt(i + 1)}
              >
                <Image
                  alt="Hotel other photos"
                  src={image}
                  key={image}
                  width="0"
                  height="0"
                  sizes="320px"
                  loading="lazy"
                  className={cn(
                    'h-[147px] w-full cursor-pointer object-cover object-center'
                  )}
                />
              </button>
            ))}
          </Masonry>
        </div>
      )}
    </ImagesLightbox>
  );
}
